import React, { useEffect, useMemo, useRef, useState } from "react";
import { ControllerReceta } from "../../../Service/Service.consulta";
import { getMedicinaService } from "../../../Service/Service.medicina";
import { GetTokenDecoded } from "../../../utils/storeUsuario";
import HCModal from "../HCModal";
import { Autocomplete, Field, PrintPreview, Section } from "../HCPiezas";

/* ============================================================================
   Agregar medicamento a la receta — migrado a HCModal.

   Payload y endpoint idénticos: POST /consulta_receta con
   { idreceta, idpaciente, idcita, iddoctor, idclinica, medicamentos,
     posologia, cantidad, dosis, indicaciones }.

   NO se añaden «Vía» ni «Frecuencia» aunque estén en la maqueta: `receta_cita`
   no tiene esas columnas y el cliente pidió dejar la receta como estaba. La
   posología sigue siendo el campo libre donde eso se escribe.

   Menos clics:
   · «Guardar y agregar otro» mantiene el modal abierto y limpia solo el
     medicamento y sus datos. Receta de tres fármacos: una apertura, no tres.
   · El vademécum se busca escribiendo y se confirma con Enter; si el fármaco
     no está en catálogo, se ofrece usarlo como texto libre en la misma lista
     (el comportamiento que ya daba CreatableSelect, sin abrir un select).
   · Vista previa de la línea tal como se imprime, para no descubrir en el PDF
     que la posología quedó ambigua.
   ========================================================================== */

const VACIO = { cantidad: "", dosis: "", posologia: "", indicaciones: "" };

export default function ModalRecetaHC({ show, onHide, cita, paciente, onGuardado }) {
  const userLocal = GetTokenDecoded();
  const [vademecum, setVademecum] = useState([]);
  const [medicamento, setMedicamento] = useState(null);
  const [campos, setCampos] = useState(VACIO);
  const [loading, setLoading] = useState(false);
  const [intentado, setIntentado] = useState(false);
  const medRef = useRef(null);

  useEffect(() => {
    if (!show) return;
    setMedicamento(null);
    setCampos(VACIO);
    setIntentado(false);
    (async () => {
      const r = await getMedicinaService(userLocal?.idclinica);
      const lista = r?.data || [];
      setVademecum(lista.map((m) => ({ value: m.descripcion, label: m.descripcion })));
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  // El vademécum llega completo, así que el filtro es local: sin ida al servidor
  // por cada letra.
  const buscar = (texto) => {
    const t = texto.toLowerCase();
    return vademecum.filter((m) => m.label.toLowerCase().includes(t)).slice(0, 50);
  };

  const set = (k) => (e) => setCampos((c) => ({ ...c, [k]: e.target.value }));

  // Mismas obligatorias que validateFormReceta: posología, cantidad y dosis.
  const faltantes = useMemo(() => {
    const f = [];
    if (!medicamento) f.push("Medicamento");
    if (!campos.cantidad.trim()) f.push("Cantidad");
    if (!campos.dosis.trim()) f.push("Dosis");
    if (!campos.posologia.trim()) f.push("Posología");
    return f;
  }, [medicamento, campos]);

  const lineaImpresa = useMemo(() => {
    if (!medicamento) return null;
    const partes = [];
    if (campos.cantidad.trim()) partes.push(`${campos.cantidad.trim()}.`);
    const pauta = [campos.dosis.trim(), campos.posologia.trim()].filter(Boolean).join(" ");
    if (pauta) partes.push(`${pauta}.`);
    if (campos.indicaciones.trim()) partes.push(campos.indicaciones.trim());
    return { nombre: medicamento.label, resto: partes.join(" ") };
  }, [medicamento, campos]);

  const guardar = async (seguirAgregando) => {
    setIntentado(true);
    if (faltantes.length > 0) {
      if (!medicamento) medRef.current?.querySelector("input")?.focus();
      else document.getElementById(`hc-rx-${faltantes[0] === "Cantidad" ? "cantidad" : faltantes[0] === "Dosis" ? "dosis" : "posologia"}`)?.focus();
      return;
    }
    setLoading(true);
    const r = await ControllerReceta({
      idreceta: 0,
      idpaciente: paciente?.idpaciente ?? cita?.pacientes?.idpaciente,
      idcita: cita?.idcita,
      iddoctor: cita?.doctor?.iddoctor,
      idclinica: userLocal?.idclinica,
      medicamentos: medicamento.value,
      posologia: campos.posologia,
      cantidad: campos.cantidad,
      dosis: campos.dosis,
      indicaciones: campos.indicaciones,
    });
    setLoading(false);
    if (!r || r.success !== true) return;

    if (onGuardado) await onGuardado();

    if (seguirAgregando) {
      setMedicamento(null);
      setCampos(VACIO);
      setIntentado(false);
      return;
    }
    onHide && onHide();
  };

  const yaRecetados = cita?.receta_cita || [];
  const subtitulo = [
    paciente?.nombres_completos || cita?.pacientes?.nombres_completos,
    paciente?.edad ? `${paciente.edad} a` : null,
    paciente?.cedula ? `CI ${paciente.cedula}` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <HCModal
      isOpen={!!show}
      size="md"
      title="Agregar medicamento a la receta"
      subtitle={subtitulo}
      validation={{ missing: faltantes }}
      primaryLabel="Guardar"
      onPrimary={() => guardar(false)}
      primaryLoading={loading}
      secondaryAction={{ label: "Guardar y agregar otro", onClick: () => guardar(true) }}
      onClose={onHide}
      onCancel={onHide}
      hayCambiosSinGuardar={Boolean(medicamento || campos.posologia || campos.cantidad || campos.dosis)}
    >
      <Section title="Medicamento">
        <Field
          label="Medicamento"
          required
          htmlFor="hc-rx-med"
          hint={!medicamento ? "Busca en el vademécum; si no está, puedes usarlo como texto libre." : null}
          error={intentado && !medicamento ? "Elige o escribe un medicamento" : null}
        >
          <div ref={medRef}>
            <Autocomplete
              id="hc-rx-med"
              valor={medicamento}
              onElegir={setMedicamento}
              onBuscar={buscar}
              permitirLibre
              minimo={2}
              placeholder="Escribe el nombre del medicamento…"
              ariaInvalid={intentado && !medicamento ? "true" : undefined}
              vacio="No está en el vademécum"
              renderOpcion={(op) => <span className="hcauto__sel-t" style={{ fontWeight: 500 }}>{op.label}</span>}
            />
          </div>
        </Field>
      </Section>

      <Section title="Prescripción">
        <div className="hcgrid hcgrid--2">
          <Field label="Cantidad" required htmlFor="hc-rx-cantidad" hint="Total a despachar (p. ej. 16 tabletas)">
            <input
              id="hc-rx-cantidad"
              type="text"
              value={campos.cantidad}
              placeholder="16 tabletas"
              aria-invalid={intentado && !campos.cantidad.trim() ? "true" : undefined}
              onChange={set("cantidad")}
            />
          </Field>
          <Field label="Dosis" required htmlFor="hc-rx-dosis" hint="Cuánto por toma (p. ej. 1 tableta)">
            <input
              id="hc-rx-dosis"
              type="text"
              value={campos.dosis}
              placeholder="1 tableta"
              aria-invalid={intentado && !campos.dosis.trim() ? "true" : undefined}
              onChange={set("dosis")}
            />
          </Field>
        </div>

        <Field
          label="Posología"
          required
          htmlFor="hc-rx-posologia"
          hint="Vía, frecuencia y duración (p. ej. vía oral cada 12 horas por 8 días)"
        >
          <input
            id="hc-rx-posologia"
            type="text"
            value={campos.posologia}
            placeholder="vía oral cada 12 horas por 8 días"
            aria-invalid={intentado && !campos.posologia.trim() ? "true" : undefined}
            onChange={set("posologia")}
          />
        </Field>

        <Field label="Indicaciones al paciente" htmlFor="hc-rx-ind">
          <textarea
            id="hc-rx-ind"
            rows={2}
            placeholder="Tomar con alimentos. No suspender antes de terminar."
            value={campos.indicaciones}
            onChange={set("indicaciones")}
          />
        </Field>

        {lineaImpresa && (
          <PrintPreview>
            <b>{lineaImpresa.nombre}</b>
            {lineaImpresa.resto ? ` — ${lineaImpresa.resto}` : ""}
          </PrintPreview>
        )}
      </Section>

      {yaRecetados.length > 0 && (
        <Section title={`Ya en la receta de esta consulta · ${yaRecetados.length}`}>
          <div className="hcya">
            {yaRecetados.map((r) => (
              <div className="hcya__fila" key={r.idreceta}>
                <span className="hcya__nom">
                  <b>{r.medicamentos}</b>
                  {r.cantidad ? ` — ${r.cantidad}` : ""}
                  {r.posologia ? `. ${r.posologia}` : ""}
                </span>
              </div>
            ))}
          </div>
        </Section>
      )}
    </HCModal>
  );
}
